import { XzPoint } from "./types";

/**
 * XZ düzlemi 2B geometri.
 * İsimler domain Segment (SegmentSpec) değil; yalnızca doğru / nokta.
 *
 * lineIntersection   — sonsuz doğrular
 * linesIntersection — t,u ∈ [0,1]
 * lineParameter      — sınırsız t
 * projectPointOnLine — clamp + LineProjection
 * pointOnLineInterval — aralık testi
 */

const EPS = 1e-6;

export function distSq(a: XzPoint, b: XzPoint): number {
  const dx = a.x - b.x;
  const dz = a.z - b.z;
  return dx * dx + dz * dz;
}

export function dist(a: XzPoint, b: XzPoint): number {
  return Math.sqrt(distSq(a, b));
}


export function pointsEqual(a: XzPoint, b: XzPoint, eps = EPS): boolean {
  return distSq(a, b) <= eps * eps;
}

export function lerp(a: XzPoint, b: XzPoint, t: number): XzPoint {
  return {
    x: a.x + (b.x - a.x) * t,
    z: a.z + (b.z - a.z) * t,
  };
}

/** Sonsuz doğru kesişimi — t,u sınırsız (parça sınırı yok). */
export function lineIntersection(
  a0: XzPoint,
  a1: XzPoint,
  b0: XzPoint,
  b1: XzPoint,
  eps = EPS,
): { t: number; u: number; point: XzPoint } | null {
  const x1 = a0.x;
  const z1 = a0.z;
  const dx1 = a1.x - x1;
  const dz1 = a1.z - z1;
  const x2 = b0.x;
  const z2 = b0.z;
  const dx2 = b1.x - x2;
  const dz2 = b1.z - z2;

  const denom = dx1 * dz2 - dz1 * dx2;
  if (Math.abs(denom) < eps) return null;

  const t = ((x2 - x1) * dz2 - (z2 - z1) * dx2) / denom;
  const u = ((x2 - x1) * dz1 - (z2 - z1) * dx1) / denom;

  return {
    t,
    u,
    point: lerp(a0, a1, t),
  };
}

/** İki doğru (p0–p1 ve q0–q1 aralıkları) kesişimi: t,u ∈ [0,1]. */
export function linesIntersection(
  a0: XzPoint,
  a1: XzPoint,
  b0: XzPoint,
  b1: XzPoint,
  eps = EPS,
): { t: number; u: number; point: XzPoint } | null {
  const hit = lineIntersection(a0, a1, b0, b1, eps);
  if (!hit) return null;
  if (hit.t < -eps || hit.t > 1 + eps || hit.u < -eps || hit.u > 1 + eps) {
    return null;
  }
  const tc = Math.min(1, Math.max(0, hit.t));
  const uc = Math.min(1, Math.max(0, hit.u));
  return {
    t: tc,
    u: uc,
    point: lerp(a0, a1, tc),
  };
}

/** p0 + t*(p1-p0) için sınırsız t. */
export function lineParameter(
  q: XzPoint,
  p0: XzPoint,
  p1: XzPoint,
  eps = EPS,
): number {
  const dx = p1.x - p0.x;
  const dz = p1.z - p0.z;
  const lenSq = dx * dx + dz * dz;
  if (lenSq < eps * eps) return 0;
  return ((q.x - p0.x) * dx + (q.z - p0.z) * dz) / lenSq;
}

export interface LineProjection {
  distance: number;
  point: XzPoint;
  t: number;
}

/** p noktasını a-b doğrusuna project eder; t [0,1] clamp'lenir. */
export function projectPointOnLine(
  p: XzPoint,
  a: XzPoint,
  b: XzPoint,
): LineProjection {
  const t = Math.max(0, Math.min(1, lineParameter(p, a, b, 1e-6)));
  const point = lerp(a, b, t);
  return {
    distance: dist(p, point),
    point,
    t,
  };
}

/** t ∈ (endpointEps, 1 - endpointEps) — doğru parçası gövdesi, uçlar hariç. */
export function isOnLineBody(t: number, endpointEps: number): boolean {
  return t > endpointEps && t < 1 - endpointEps;
}

/** t uç bandında mı? */
export function isNearLineEndpoint(t: number, endpointEps: number): boolean {
  return t <= endpointEps || t >= 1 - endpointEps;
}

export function isPointInPolygon(p: XzPoint, ring: XzPoint[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i].x;
    const zi = ring[i].z;
    const xj = ring[j].x;
    const zj = ring[j].z;
    const intersects =
      zi > p.z !== zj > p.z && p.x < ((xj - xi) * (p.z - zi)) / (zj - zi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}


/** q, p0–p1 aralığında mı? (değilse null) */
export function pointOnLineInterval(
  q: XzPoint,
  p0: XzPoint,
  p1: XzPoint,
  eps = EPS,
): number | null {
  const dx = p1.x - p0.x;
  const dz = p1.z - p0.z;
  const lenSq = dx * dx + dz * dz;
  if (lenSq < eps * eps) return null;

  const t = lineParameter(q, p0, p1, eps);
  if (t < -eps || t > 1 + eps) return null;

  const proj = lerp(p0, p1, t);
  if (distSq(q, proj) > eps * eps) return null;
  return Math.min(1, Math.max(0, t));
}

export function mergeVertices(
  points: XzPoint[],
  eps = EPS,
): { vertices: XzPoint[]; indexMap: number[] } {
  const vertices: XzPoint[] = [];
  const indexMap: number[] = [];

  for (const p of points) {
    const existing = vertices.findIndex((v) => pointsEqual(v, p, eps));
    if (existing >= 0) {
      indexMap.push(existing);
    } else {
      indexMap.push(vertices.length);
      vertices.push({ x: p.x, z: p.z });
    }
  }

  return { vertices, indexMap };
}
